/** Built-in document templates shipped with Tier 3 Group D Slice 1c.
 *
 *  Each template is a canvas-size preset: width × height in pixels
 *  plus optional DPI metadata for print pipelines. They're grouped
 *  into the four categories the picker dialog surfaces as tabs.
 *
 *  V1 scope: built-in templates carry size + DPI only (no starter
 *  layers). The roadmap calls for "optional starter layers" — that's
 *  a future enhancement gated on a serialization adapter that can
 *  spawn a fresh `documentId`-keyed `PersistedSession` blob from a
 *  template snapshot. See `lib/user-templates-prefs.ts` for the
 *  same scoping rationale on user templates. */

export type TemplateCategory = 'social' | 'print' | 'web' | 'devices' | 'custom';

export interface DocumentTemplate {
  /** Stable id (`builtin:<slug>` for built-ins, `user:<rand>` for
   *  user-saved). Used as React keys and the localStorage cursor. */
  id: string;
  /** Display name. User-renameable for user templates; immutable
   *  for built-ins. */
  name: string;
  category: TemplateCategory;
  kind: 'builtin' | 'user';
  /** Canvas width in pixels. */
  width: number;
  /** Canvas height in pixels. */
  height: number;
  /** DPI metadata. Optional — built-in print templates carry it;
   *  web / device templates don't (DPI is meaningless for raster
   *  output bound to a pixel viewport). */
  dpi?: number;
  /** Short tagline shown in the template grid card. */
  description?: string;
}

export const TEMPLATE_CATEGORY_LABEL: Record<TemplateCategory, string> = {
  social: 'Social Media',
  print: 'Print',
  web: 'Web',
  devices: 'Devices',
  custom: 'My templates',
};

/** Built-in templates, in display order within each category. The
 *  picker dialog groups them by `category`; sort within a category
 *  is the array order here. */
export const BUILTIN_TEMPLATES: ReadonlyArray<DocumentTemplate> = [
  // ─── Social media ─────────────────────────────────────────────
  {
    id: 'builtin:ig-post',
    name: 'Instagram post',
    category: 'social',
    kind: 'builtin',
    width: 1080,
    height: 1080,
    description: '1:1 square',
  },
  {
    id: 'builtin:ig-portrait',
    name: 'Instagram portrait',
    category: 'social',
    kind: 'builtin',
    width: 1080,
    height: 1350,
    description: '4:5 portrait',
  },
  {
    id: 'builtin:ig-story',
    name: 'Instagram story',
    category: 'social',
    kind: 'builtin',
    width: 1080,
    height: 1920,
    description: '9:16 vertical',
  },
  {
    id: 'builtin:twitter-post',
    name: 'X / Twitter post',
    category: 'social',
    kind: 'builtin',
    width: 1200,
    height: 675,
    description: '16:9 landscape',
  },
  {
    id: 'builtin:fb-cover',
    name: 'Facebook cover',
    category: 'social',
    kind: 'builtin',
    width: 820,
    height: 312,
  },
  {
    id: 'builtin:yt-thumbnail',
    name: 'YouTube thumbnail',
    category: 'social',
    kind: 'builtin',
    width: 1280,
    height: 720,
    description: '16:9 thumbnail',
  },
  {
    id: 'builtin:linkedin-banner',
    name: 'LinkedIn banner',
    category: 'social',
    kind: 'builtin',
    width: 1584,
    height: 396,
  },
  {
    id: 'builtin:tiktok',
    name: 'TikTok video',
    category: 'social',
    kind: 'builtin',
    width: 1080,
    height: 1920,
    description: '9:16 vertical',
  },

  // ─── Print ────────────────────────────────────────────────────
  {
    id: 'builtin:letter-300',
    name: 'US Letter',
    category: 'print',
    kind: 'builtin',
    width: 2550,
    height: 3300,
    dpi: 300,
    description: '8.5 × 11 in @ 300 DPI',
  },
  {
    id: 'builtin:a4-300',
    name: 'A4',
    category: 'print',
    kind: 'builtin',
    width: 2480,
    height: 3508,
    dpi: 300,
    description: '210 × 297 mm @ 300 DPI',
  },
  {
    id: 'builtin:a5-300',
    name: 'A5',
    category: 'print',
    kind: 'builtin',
    width: 1748,
    height: 2480,
    dpi: 300,
    description: '148 × 210 mm @ 300 DPI',
  },
  {
    id: 'builtin:postcard-300',
    name: 'Postcard',
    category: 'print',
    kind: 'builtin',
    width: 1800,
    height: 1200,
    dpi: 300,
    description: '6 × 4 in @ 300 DPI',
  },
  {
    id: 'builtin:business-card-300',
    name: 'Business card',
    category: 'print',
    kind: 'builtin',
    width: 1050,
    height: 600,
    dpi: 300,
    description: '3.5 × 2 in @ 300 DPI',
  },
  {
    id: 'builtin:poster-300',
    name: 'Poster (18 × 24)',
    category: 'print',
    kind: 'builtin',
    width: 5400,
    height: 7200,
    dpi: 300,
    description: '18 × 24 in @ 300 DPI',
  },

  // ─── Web ──────────────────────────────────────────────────────
  {
    id: 'builtin:web-fhd',
    name: 'Full HD',
    category: 'web',
    kind: 'builtin',
    width: 1920,
    height: 1080,
    description: '1920 × 1080',
  },
  {
    id: 'builtin:web-hd',
    name: 'HD 720p',
    category: 'web',
    kind: 'builtin',
    width: 1280,
    height: 720,
  },
  {
    id: 'builtin:web-qhd',
    name: 'QHD',
    category: 'web',
    kind: 'builtin',
    width: 2560,
    height: 1440,
  },
  {
    id: 'builtin:web-4k',
    name: '4K UHD',
    category: 'web',
    kind: 'builtin',
    width: 3840,
    height: 2160,
  },
  {
    id: 'builtin:web-laptop',
    name: 'Laptop',
    category: 'web',
    kind: 'builtin',
    width: 1440,
    height: 900,
  },
  {
    id: 'builtin:web-banner',
    name: 'Web banner',
    category: 'web',
    kind: 'builtin',
    width: 1920,
    height: 480,
    description: '4:1 hero',
  },

  // ─── Devices ──────────────────────────────────────────────────
  {
    id: 'builtin:iphone-14',
    name: 'iPhone 14 / 15',
    category: 'devices',
    kind: 'builtin',
    width: 1170,
    height: 2532,
    description: '3x native',
  },
  {
    id: 'builtin:iphone-se',
    name: 'iPhone SE',
    category: 'devices',
    kind: 'builtin',
    width: 750,
    height: 1334,
    description: '2x native',
  },
  {
    id: 'builtin:ipad-pro',
    name: 'iPad Pro 12.9"',
    category: 'devices',
    kind: 'builtin',
    width: 2048,
    height: 2732,
    description: '2x native',
  },
  {
    id: 'builtin:pixel-7',
    name: 'Pixel 7',
    category: 'devices',
    kind: 'builtin',
    width: 1080,
    height: 2400,
  },
  {
    id: 'builtin:apple-watch',
    name: 'Apple Watch Ultra',
    category: 'devices',
    kind: 'builtin',
    width: 410,
    height: 502,
  },
  {
    id: 'builtin:macbook-pro-14',
    name: 'MacBook Pro 14"',
    category: 'devices',
    kind: 'builtin',
    width: 3024,
    height: 1964,
  },
];

/** Lookup helper — returns `undefined` for unknown ids. */
export function getBuiltinTemplate(id: string): DocumentTemplate | undefined {
  return BUILTIN_TEMPLATES.find((t) => t.id === id);
}
